import { type Dispatch, type SetStateAction, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { isTeamWageSettingsRequested, TEAM_WAGE_SETTINGS_PARAMS } from "@/components/team/WageSettings/deepLink";
import { useLatestRef } from "@/hooks/useLatestRef";
import { backendService } from "@/services/backend.service";
import { trackErrorEvent, trackEvent } from "@/utils/analytics";
import { requestDepartmentTokenWithRetry } from "@/utils/departmentToken";
import type { UserView } from "@/utils/models";
import { getSessionEpoch, getTokenDepartmentId, setTokenDepartmentId } from "@/utils/networkWrapper";
import { type Department, type Token, type UserInfo, useAuth } from "../AuthProvider";
import { useSnackbar } from "../SnackbarContext";
import { DEFAULT_CHOSEN_DEPARTMENT } from "./consts";

/** A department's token and the user info read with it, minted against the session of `epoch`. */
type DepartmentSession = { token: Token; userInfo: UserInfo; department: Department; epoch: number };

/** Mints `departmentId`'s token and reads the user info carrying that department's full config. */
const fetchDepartmentSession = async (departmentId: string, view: UserView | undefined): Promise<DepartmentSession> => {
	const epoch = getSessionEpoch();
	if (!view) throw new Error("the user info carries no view to mint the department token for");
	const token = await requestDepartmentTokenWithRetry(departmentId, view);
	if (!token.access_token) throw new Error(`/token/department returned no access token for ${departmentId}`);
	const { data: userInfo } = await backendService.getUserInfoForToken(token.access_token, departmentId);
	const department = userInfo.departments.find((dept) => dept.department_id === departmentId);
	if (!department) throw new Error(`the user info for ${departmentId}'s token does not list it`);
	return { token, userInfo, department, epoch };
};

/** `/A/staffing/weekly?x=1` → `/B/staffing/weekly?x=1`, or null when the path is not under department A. */
const pathInDepartment = (pathname: string, search: string, fromDepartmentId: string, toDepartmentId: string): string | null => {
	const [, firstSegment, ...rest] = pathname.split("/");
	return firstSegment === fromDepartmentId ? `${["", toDepartmentId, ...rest].join("/")}${search}` : null;
};

type NavigationState = { pathname: string; search: string; chosenDepartmentId: string };

/** Team consumes a Wage Settings deep link by removing its one-shot parameters in place. */
const isTeamDeepLinkConsumed = (previous: NavigationState, current: NavigationState): boolean => {
	if (previous.pathname !== current.pathname || previous.chosenDepartmentId !== current.chosenDepartmentId) return false;
	if (previous.pathname.split("/")[2] !== "team") return false;
	const before = new URLSearchParams(previous.search);
	if (!isTeamWageSettingsRequested(before)) return false;
	for (const param of TEAM_WAGE_SETTINGS_PARAMS) before.delete(param);
	return before.toString() === new URLSearchParams(current.search).toString();
};

/**
 * Puts a department on screen only once this tab's token opens it — in admin view a token opens one
 * department, and showing another first 403s every department request until the token catches up.
 * The token, the user info read with it and the department are installed in one render.
 *
 * `adoptDepartment` follows the URL (Back/Forward, links, reloads) and mints when it must.
 * `prepareDepartment` is the header switch's: it mints up front and throws on failure, so the switch
 * can stay put; the adoption its navigation then triggers installs the prepared session.
 */
export const useAdoptDepartment = (chosenDepartmentId: string, setChosenDepartment: Dispatch<SetStateAction<Department>>) => {
	const { userInfo, installDepartmentSession } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const latest = useLatestRef({ chosenDepartmentId, userInfo, installDepartmentSession, showSnackbar, t, navigate, pathname, search });
	const navigationGenerationRef = useRef(0);
	const previousNavigationRef = useRef<NavigationState>({ pathname, search, chosenDepartmentId });
	useLayoutEffect(() => {
		const previous = previousNavigationRef.current;
		const current = { pathname, search, chosenDepartmentId };
		if (
			(previous.pathname !== pathname || previous.search !== search || previous.chosenDepartmentId !== chosenDepartmentId) &&
			!isTeamDeepLinkConsumed(previous, current)
		) {
			navigationGenerationRef.current += 1;
		}
		previousNavigationRef.current = current;
	}, [pathname, search, chosenDepartmentId]);
	// The department last asked for, the one being minted, and one the header switch minted ahead.
	const wantedRef = useRef<string | null>(null);
	const pendingRef = useRef<string | null>(null);
	const preparedRef = useRef<DepartmentSession | null>(null);
	// Counts header switches, so only the last one clicked goes ahead when several are in flight.
	const switchCountRef = useRef(0);
	// A logout unmounts this; a mint still in flight must not install or retry into the next session.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const install = useCallback(
		(session: DepartmentSession) => {
			const departmentId = session.department.department_id;
			latest.current.installDepartmentSession(session.token, departmentId, session.userInfo);
			setChosenDepartment(session.department);
			trackEvent("department_token_sync_success", { department_id: departmentId });
		},
		[latest, setChosenDepartment],
	);

	/** Back to the department this tab's current token is confirmed to open. */
	const fallBack = useCallback(
		(failedDepartment: Department) => {
			const failedDepartmentId = failedDepartment.department_id;
			const current = latest.current;
			current.showSnackbar(current.t("error.department.switch.failed"), "error");
			// DEFAULT: /user_info with no department query loads the token's first department and puts
			// it first in admin view; this is the safe recovery when /refresh omitted department metadata.
			const tokenDepartmentId = getTokenDepartmentId();
			const fallbackDepartmentId = tokenDepartmentId ?? current.userInfo.departments[0]?.department_id;
			const fallbackDepartment = current.userInfo.departments.find((dept) => dept.department_id === fallbackDepartmentId);
			if (!fallbackDepartment) {
				// DEFAULT: an invalid session with no token-backed department cannot open a department page.
				setChosenDepartment(DEFAULT_CHOSEN_DEPARTMENT);
				current.navigate("/login", { replace: true });
				return;
			}
			if (!tokenDepartmentId) setTokenDepartmentId(fallbackDepartment.department_id);
			wantedRef.current = fallbackDepartment.department_id;
			if (fallbackDepartmentId === failedDepartmentId) {
				setChosenDepartment(fallbackDepartment);
				return;
			}
			const fallbackPath = pathInDepartment(current.pathname, current.search, failedDepartmentId, fallbackDepartment.department_id);
			if (fallbackPath) {
				current.navigate(fallbackPath, { replace: true });
				return;
			}
			setChosenDepartment(fallbackDepartment);
		},
		[latest, setChosenDepartment],
	);

	const openDepartment = useCallback(
		async (department: Department): Promise<void> => {
			const departmentId = department.department_id;
			pendingRef.current = departmentId;
			let superseded = false;
			trackEvent("department_token_sync_start", { department_id: departmentId, token_department_id: getTokenDepartmentId() });
			try {
				const session = await fetchDepartmentSession(departmentId, latest.current.userInfo.view);
				// A newer department request owns the screen, or a sign-in or view switch installed its own token.
				superseded = !mountedRef.current || wantedRef.current !== departmentId || getSessionEpoch() !== session.epoch;
				if (superseded) trackEvent("department_token_sync_superseded", { department_id: departmentId });
				else install(session);
			} catch (error) {
				trackErrorEvent("department_token_sync_error", error, { department_id: departmentId });
				if (mountedRef.current && wantedRef.current === departmentId) fallBack(department);
			} finally {
				if (pendingRef.current === departmentId) pendingRef.current = null;
			}
			// Still asked for, but minted against a session another flow has since replaced.
			if (superseded && mountedRef.current && wantedRef.current === departmentId && getTokenDepartmentId() !== departmentId) {
				await openDepartment(department);
			}
		},
		[latest, install, fallBack],
	);

	const adoptDepartment = useCallback(
		(department: Department) => {
			const departmentId = department.department_id;
			wantedRef.current = departmentId;
			const prepared = preparedRef.current;
			preparedRef.current = null;
			const { chosenDepartmentId: shownDepartmentId, userInfo: currentUserInfo } = latest.current;
			if (departmentId === shownDepartmentId || pendingRef.current === departmentId) return;
			if (prepared?.department.department_id === departmentId && prepared.epoch === getSessionEpoch()) {
				install(prepared);
				return;
			}
			// While impersonating, /token/department authenticates with the refresh token, which belongs
			// to the SUPPORT user — minting would end the impersonation.
			if (currentUserInfo.impersonated_by || getTokenDepartmentId() === departmentId) {
				setChosenDepartment(department);
				return;
			}
			void openDepartment(department);
		},
		[latest, install, openDepartment, setChosenDepartment],
	);

	/**
	 * Resolves true when the switch should go ahead, false when a later switch, a logout or another
	 * session replaced it while it minted; throws when the department cannot be opened.
	 */
	const prepareDepartment = useCallback(
		async (department: Department): Promise<boolean> => {
			const switchNumber = ++switchCountRef.current;
			const sourceNavigationGeneration = navigationGenerationRef.current;
			const sourceEpoch = getSessionEpoch();
			// Nothing to mint while impersonating; the adoption shows the department on the token it has.
			if (latest.current.userInfo.impersonated_by) return true;
			const departmentId = department.department_id;
			trackEvent("department_token_sync_start", { department_id: departmentId, token_department_id: getTokenDepartmentId() });
			let session: DepartmentSession;
			try {
				session = await fetchDepartmentSession(departmentId, latest.current.userInfo.view);
			} catch (error) {
				trackErrorEvent("department_token_sync_error", error, { department_id: departmentId });
				if (
					!mountedRef.current ||
					switchNumber !== switchCountRef.current ||
					navigationGenerationRef.current !== sourceNavigationGeneration ||
					getSessionEpoch() !== sourceEpoch
				) {
					trackEvent("department_token_sync_superseded", { department_id: departmentId });
					return false;
				}
				throw error;
			}
			if (
				!mountedRef.current ||
				switchNumber !== switchCountRef.current ||
				navigationGenerationRef.current !== sourceNavigationGeneration ||
				getSessionEpoch() !== session.epoch
			) {
				trackEvent("department_token_sync_superseded", { department_id: departmentId });
				return false;
			}
			preparedRef.current = session;
			return true;
		},
		[latest],
	);

	return { adoptDepartment, prepareDepartment };
};
